import { assertErrors, fetchAPI, METHODS, parseBody } from './common';
import qs from 'qs';

function normalizePaginatedPayload(payload) {
  if (Array.isArray(payload)) {
    return { results: payload, count: payload.length };
  }
  if (!payload) {
    return { count: 0, results: [] };
  }
  const results = Array.isArray(payload.results) ? payload.results : [];
  const count =
    typeof payload.count === 'number' ? payload.count : results.length;
  return { results, count };
}

function resolveDateParam(value) {
  if (!value) {
    return null;
  }
  return value instanceof Date ? value.toISOString() : String(value);
}

/**
 * Org posts with reservation summaries (GET /freshcue/org/posts).
 * @param {{teamIds?: string[], scope?: 'active'|'past', page?: number, pageSize?: number}} params
 * @returns {Promise<{results: Array, count: number}>}
 */
export async function getOrgPosts({
  teamIds,
  scope = 'active',
  page,
  pageSize,
  startDate,
  endDate,
} = {}) {
  const params = { scope };
  const resolvedStart = resolveDateParam(startDate);
  const resolvedEnd = resolveDateParam(endDate);
  if (resolvedStart) params.startDate = resolvedStart;
  if (resolvedEnd) params.endDate = resolvedEnd;
  if (teamIds?.length) {
    params.teamIds = teamIds.join(',');
  }
  if (page !== undefined) {
    params.page = String(page);
  }
  if (pageSize !== undefined) {
    params.pageSize = String(pageSize);
  }
  const res = await fetchAPI(
    `/freshcue/org/posts?${qs.stringify(params)}`,
    METHODS.get
  );
  await assertErrors(res);
  const body = await parseBody(res);
  return normalizePaginatedPayload(body.posts);
}

/**
 * Fetches Freshcue posts visible to the current business.
 * @returns {Promise<Array>}
 */
export async function getPosts({ page, pageSize, startDate, endDate } = {}) {
  const params = {};
  if (page !== undefined) {
    params.page = String(page);
  }
  if (pageSize !== undefined) {
    params.pageSize = String(pageSize);
  }
  const resolvedStart = resolveDateParam(startDate);
  const resolvedEnd = resolveDateParam(endDate);
  if (resolvedStart) {
    params.startDate = resolvedStart;
  }
  if (resolvedEnd) {
    params.endDate = resolvedEnd;
  }

  const queryString = qs.stringify(params);
  const route = queryString
    ? `/freshcue/posts?${queryString}`
    : '/freshcue/posts';
  const res = await fetchAPI(route, METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return normalizePaginatedPayload(body.posts);
}

export async function getAllPosts({ page, pageSize, filterModel } = {}) {
  const params = {};
  if (page !== undefined && page !== null) {
    params.page = String(page);
  }
  if (pageSize !== undefined && pageSize !== null) {
    params.pageSize = String(pageSize);
  }

  const queryString = qs.stringify(params);
  const route = queryString
    ? `/freshcue/posts/all?${queryString}`
    : '/freshcue/posts/all';
  const res = await fetchAPI(route, METHODS.post, {
    filterModel,
  });
  await assertErrors(res);
  const body = await parseBody(res);
  return normalizePaginatedPayload(body.posts);
}

export async function getPostReservations({
  postId,
  page,
  pageSize,
  filterModel,
} = {}) {
  if (!postId) {
    throw new Error('postId is required');
  }

  const params = {};
  if (page !== undefined && page !== null) {
    params.page = String(page);
  }
  if (pageSize !== undefined && pageSize !== null) {
    params.pageSize = String(pageSize);
  }

  const queryString = qs.stringify(params);
  const route = queryString
    ? `/freshcue/posts/${postId}/reservations/all?${queryString}`
    : `/freshcue/posts/${postId}/reservations/all`;

  const res = await fetchAPI(route, METHODS.post, {
    filterModel,
  });
  await assertErrors(res);
  const body = await parseBody(res);
  return normalizePaginatedPayload(body.reservations);
}

export async function getPostDetails({ postId } = {}) {
  if (!postId) {
    throw new Error('postId is required');
  }

  const res = await fetchAPI(`/freshcue/posts/${postId}`, METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.post ?? null;
}

/**
 * Fetches nearby Freshcue organizations for this business.
 * @param {object} [options]
 * @param {string} [options.search]
 * @param {number} [options.limit]
 * @param {number} [options.offset]
 * @returns {Promise<Array>}
 */
export async function getAvailableBusinessOrganizations({
  search,
  limit,
  offset,
} = {}) {
  const params = {};
  if (search && typeof search === 'string' && search.trim()) {
    params.search = search.trim();
  }
  if (limit !== undefined && limit !== null) {
    params.limit = String(limit);
  }
  if (offset !== undefined && offset !== null) {
    params.offset = String(offset);
  }

  const queryString = qs.stringify(params, {
    skipNull: true,
    encodeValuesOnly: true,
  });
  const route = queryString
    ? `/freshcue/organizations/nearby?${queryString}`
    : '/freshcue/organizations/nearby';

  const res = await fetchAPI(route, METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.organizations ?? [];
}

/**
 * Fetches the orgs the current user can post for, with their approved programs.
 * @returns {Promise<Array>}
 */
/**
 * Food types the backend accepts for a Freshcue quick post
 * (GET /freshcue/food-types). Names are upper case, e.g. 'NON-PERISHABLE'.
 * @returns {Promise<Array<{id: string, name: string}>>}
 */
export async function getFoodTypes() {
  const res = await fetchAPI('/freshcue/food-types', METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.foodTypes ?? [];
}

export async function getOrgAccess() {
  const res = await fetchAPI('/freshcue/me/org-access', METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.orgs ?? [];
}

/**
 * Creates a new Freshcue post on behalf of the current business.
 * @param {object} post
 * @returns {Promise<object>}
 */
export async function createPost(post) {
  const res = await fetchAPI(`/freshcue/posts`, METHODS.post, post);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.post;
}

/**
 * Creates a quick offer with a draft inventory item in a single step.
 * Used for one-time offers like catered event leftovers.
 * @param {object} params
 * @returns {Promise<object>}
 */
export async function createQuickPost(params) {
  const res = await fetchAPI(`/freshcue/posts/quick`, METHODS.post, params);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.post;
}

/**
 * Updates an existing Freshcue post. Requires the business that owns the post.
 * @param {object} params
 * @param {string} params.postId
 * @returns {Promise<object>}
 */
export async function updatePost({ postId, ...post }) {
  if (!postId) {
    throw new Error('postId is required');
  }

  const res = await fetchAPI(`/freshcue/posts/${postId}`, METHODS.put, post);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.post;
}

/**
 * Fetches pending Freshcue posts for the current organization.
 * @returns {Promise<Array>}
 */
export async function getPendingPosts(paginationParams) {
  const res = await fetchAPI(
    `/freshcue/posts/pending?${qs.stringify(paginationParams)}`,
    METHODS.get
  );
  await assertErrors(res);
  const body = await parseBody(res);
  return normalizePaginatedPayload(body.posts);
}

/**
 * Responds to a pending Freshcue post.
 * @param {object} params
 * @param {string} params.postId
 * @param {'ACCEPT'|'REJECT'|'IGNORE'} params.action
 * @returns {Promise<object>}
 */
export async function respondToPost({ postId, action }) {
  const res = await fetchAPI(
    `/freshcue/posts/${postId}/respond`,
    METHODS.post,
    { action }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function removePost(postId) {
  const res = await fetchAPI(`/freshcue/posts/${postId}`, METHODS.delete);
  await assertErrors(res);
  const body = await parseBody(res);
  return body.post;
}

export async function removeRecipientFromPost(postId) {
  const res = await fetchAPI(
    `/freshcue/posts/${postId}/recipient`,
    METHODS.delete
  );
  await assertErrors(res);
  const body = await parseBody(res);
  return body;
}

export async function getBusinessReservations({ page, pageSize } = {}) {
  const params = {};
  if (page !== undefined) {
    params.page = String(page);
  }
  if (pageSize !== undefined) {
    params.pageSize = String(pageSize);
  }

  const queryString = qs.stringify(params);
  const route = queryString
    ? `/freshcue/businesses/reservations?${queryString}`
    : '/freshcue/businesses/reservations';
  const res = await fetchAPI(route, METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return normalizePaginatedPayload(body);
}

async function updateBusinessReservationStatus({ reservationId, status }) {
  const res = await fetchAPI(
    `/freshcue/businesses/reservations/${reservationId}`,
    METHODS.put,
    { status }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function cancelBusinessReservation({ reservationId }) {
  return updateBusinessReservationStatus({
    reservationId,
    status: 'CANCELLED',
  });
}

export async function markBusinessReservationPickedUp({ reservationId }) {
  return updateBusinessReservationStatus({
    reservationId,
    status: 'PICKED_UP',
  });
}

/**
 * Fetches offer statistics for the current Freshcue organization.
 * @returns {Promise<{currentCount: number, upcomingCount: number}>}
 */
export async function getOfferStats() {
  const res = await fetchAPI('/freshcue/stats/offers', METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return {
    currentCount: body.currentCount ?? 0,
    upcomingCount: body.upcomingCount ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Program donors (program admin/staff of the session team)
// ---------------------------------------------------------------------------

/**
 * The session program's donor rows (open invites, links, blocks, history) and
 * `recentDonors`: nearby orgs without a link that posted to the program in the
 * last 90 days.
 * @returns {Promise<{donors: Array, recentDonors: Array}>}
 */
export async function getProgramDonors() {
  const res = await fetchAPI('/freshcue/donors', METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return { donors: body.donors ?? [], recentDonors: body.recentDonors ?? [] };
}

/**
 * Per-donor activity stats for the session program over a date range.
 * @param {{startDate: string, endDate: string}} range YYYY-MM-DD
 * @returns {Promise<{rows: Array, totals: Object}>}
 */
export async function getDonorStats({ startDate, endDate }) {
  const res = await fetchAPI(
    `/freshcue/stats/donors?${qs.stringify({ startDate, endDate })}`,
    METHODS.get
  );
  await assertErrors(res);
  const body = await parseBody(res);
  return { rows: body.rows ?? [], totals: body.totals ?? {} };
}

/**
 * Links an existing org whose admin owns `email` (an `existing_org` invite
 * suggestion) right away; the program vouches for it.
 * @param {{email: string, donorTeamId: string, relationship: 'internal'|'external'}} params
 * @returns {Promise<{donor: object, alreadyLinked: boolean}>}
 */
export async function linkProgramDonor({ email, donorTeamId, relationship }) {
  const res = await fetchAPI('/freshcue/donors/links', METHODS.post, {
    email,
    donorTeamId,
    relationship,
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Blocks a donor org from posting to the session program. Its active posts to
 * the program are cancelled or trimmed.
 * @param {string} donorTeamId
 * @returns {Promise<{donor: object, revocation: object}>}
 */
export async function blockProgramDonor(donorTeamId) {
  const res = await fetchAPI('/freshcue/donors/block', METHODS.post, {
    donorTeamId,
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Lifts a block.
 * @param {string} donorId the blocked row id
 * @returns {Promise<{donor: object}>}
 */
export async function unblockProgramDonor(donorId) {
  const res = await fetchAPI(
    `/freshcue/donors/${encodeURIComponent(donorId)}/unblock`,
    METHODS.post
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Invites a donor org by email. Resolves to `{ donor }` when the invite was
 * sent, or `{ suggestion: { type, orgs } }` when a matching org already exists
 * (pass `force: true` to send anyway).
 * @param {{email: string, orgName: string, relationship: string, force?: boolean}} params
 * @returns {Promise<{donor?: object, suggestion?: {type: string, orgs: Array}}>}
 */
export async function inviteProgramDonor({
  email,
  orgName,
  relationship,
  force,
}) {
  const res = await fetchAPI('/freshcue/donors/invites', METHODS.post, {
    email,
    orgName,
    relationship,
    ...(force ? { force: true } : {}),
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Re-sends an open donor invite email.
 * @param {string} donorId
 * @returns {Promise<object>} the donor row
 */
export async function resendProgramDonorInvite(donorId) {
  const res = await fetchAPI(
    `/freshcue/donors/${donorId}/resend`,
    METHODS.post
  );
  await assertErrors(res);
  const body = await parseBody(res);
  return body.donor;
}

/**
 * Removes a donor link or cancels an open invite.
 * @param {string} donorId
 * @returns {Promise<{donor: object, revocation: object|null}>}
 */
export async function removeProgramDonor(donorId) {
  const res = await fetchAPI(`/freshcue/donors/${donorId}`, METHODS.delete);
  await assertErrors(res);
  return parseBody(res);
}

// ---------------------------------------------------------------------------
// Donor invites (invitee side)
// ---------------------------------------------------------------------------

/**
 * Public details of a donor invite. Rejects with `status`/`code` set:
 * 404 INVITE_NOT_FOUND, 410 INVITE_EXPIRED.
 * @param {string} token
 * @returns {Promise<{programName: string, orgName: string, email: string, relationship: string, status: string}>}
 */
export async function getDonorInvite(token) {
  const res = await fetchAPI(
    `/freshcue/donors/invites/${encodeURIComponent(token)}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Accepts a donor invite on behalf of an org the user administers. The org is
 * linked to the program right away (`approved`); `orgPendingApproval` is true
 * while the org's own Careit signup still waits for moderation.
 * @param {{token: string, donorTeamId: string}} params
 * @returns {Promise<{donor: object, alreadyLinked: boolean, orgPendingApproval: boolean}>}
 */
export async function acceptDonorInvite({ token, donorTeamId }) {
  const res = await fetchAPI(
    `/freshcue/donors/invites/${encodeURIComponent(token)}/accept`,
    METHODS.post,
    { donorTeamId }
  );
  await assertErrors(res);
  return parseBody(res);
}
